import { Controller } from "@hotwired/stimulus"

// vortex_images: Bilder spawnen im Zentrum (winzig, transparent),
// wachsen und spiralisieren nach außen bis zum Bildschirmrand.
// Jedes Bild dreht sich langsam um seine eigene Achse.
// Maus: nur sehr subtile Verschiebung des Wirbelmittelpunkts.
// Richtung: innen → außen (Zentrum = Ursprung, Rand = Ziel)

export default class extends Controller {
  static targets = ["canvas"]
  static values = {
    images:  { type: Array,  default: [] },
    count:   { type: Number, default: 30 },
    centerY: { type: Number, default: 0.42 }
  }

  connect() {
    this.canvas = this.canvasTarget
    this.ctx    = this.canvas.getContext("2d")

    this.center       = { x: 0, y: 0 }
    this.targetCenter = { x: 0, y: 0 }
    this.particles    = []
    this.imgs         = []
    this.raf          = null

    this.resize()
    this.loadImages().then(() => {
      this.spawnParticles()
      this.addListeners()
      this.tick()
    })
  }

  disconnect() {
    cancelAnimationFrame(this.raf)
    window.removeEventListener("resize", this._onResize)
  }

  // ── Sizing ────────────────────────────────────────────────────────────────

  resize() {
    const rect     = this.element.getBoundingClientRect()
    this.canvas.width  = rect.width
    this.canvas.height = rect.height
    this.W  = this.canvas.width
    this.H  = this.canvas.height
    this.cx = this.W / 2
    this.cy = this.H * this.centerYValue
    this.center.x       = this.cx
    this.center.y       = this.cy
    this.targetCenter.x = this.cx
    this.targetCenter.y = this.cy
    // Bis zur Ecke — Bilder fliegen wirklich bis zum Rand
    this.maxR = Math.hypot(this.W / 2, this.H / 2)
    if (this.particles.length) this.spawnParticles()
  }

  // ── Bilder laden ──────────────────────────────────────────────────────────

  async loadImages() {
    if (!this.imagesValue.length) return
    const loaded = await Promise.all(
      this.imagesValue.map(url => new Promise(resolve => {
        const img = new Image()
        img.crossOrigin = "anonymous"
        img.onload  = () => resolve(img)
        img.onerror = () => resolve(null)
        img.src = url
      }))
    )
    this.imgs = loaded.filter(Boolean)
  }

  // ── Partikel ──────────────────────────────────────────────────────────────

  spawnParticles() {
    if (!this.imgs.length) return
    // Gleichmäßig über alle Radien verteilt — kein Ruck beim Start
    this.particles = Array.from({ length: this.countValue }, (_, i) => {
      const t = (i + Math.random()) / this.countValue
      return this.makeParticle(t * this.maxR)
    })
  }

  makeParticle(startRadius) {
    const img = this.imgs[Math.floor(Math.random() * this.imgs.length)]
    // Reisezeit 100–150 Sekunden bei 60fps — sehr langsames Floating
    const travelFrames = 60 * (100 + Math.random() * 50)
    return {
      img,
      angle:        Math.random() * Math.PI * 2,
      radius:       startRadius ?? 0,   // spawn im Zentrum
      speedMult:    0.5 + Math.random() * 0.8,
      spiralSpeed:  this.maxR / travelFrames,
      selfRot:      Math.random() * Math.PI * 2,
      selfRotSpeed: (Math.random() - 0.5) * 0.006,
      vx: 0, vy: 0,
    }
  }

  // ── Animations-Loop ───────────────────────────────────────────────────────

  tick() {
    this.raf = requestAnimationFrame(() => this.tick())

    this.ctx.clearRect(0, 0, this.W, this.H)

    // Innere zuerst zeichnen — äußere liegen oben
    this.particles.sort((a, b) => a.radius - b.radius)

    for (const p of this.particles) {
      this.updateParticle(p)
      this.drawParticle(p)
    }
  }

  updateParticle(p) {
    // Umlaufbahn: ruhigerer Spin
    const w = 0.003 * (this.maxR / Math.max(p.radius, 60)) * p.speedMult
    p.angle   += w
    p.radius  += p.spiralSpeed   // gleichmäßiges Floating

    // Eigenrotation
    p.selfRot += p.selfRotSpeed
    p.vx = 0
    p.vy = 0

    // Respawn im Zentrum wenn Außenrand erreicht
    if (p.radius > this.maxR) {
      Object.assign(p, this.makeParticle(0))
    }
  }

  drawParticle(p) {
    // t: 0 = Zentrum, 1 = Außenrand
    const t = Math.max(0, Math.min(1, p.radius / this.maxR))

    // Opacity: früh einblenden, lange sichtbar, am Rand ausblenden
    const FADE_IN  = 0.55
    const FADE_OUT = 0.88
    let opacity
    if (t < FADE_IN) {
      opacity = (t / FADE_IN) * 0.92
    } else if (t < FADE_OUT) {
      opacity = 0.92
    } else {
      opacity = ((1 - t) / (1 - FADE_OUT)) * 0.92
    }
    if (opacity <= 0.01) return

    // Größe: linear wachsen
    const scale = t
    const w = 360 * scale
    const h = 202 * scale
    if (w < 4) return

    // Position auf der Wirbelbahn (Y komprimiert für Tiefenwirkung) + Abstoßungs-Offset
    const px = this.center.x + Math.cos(p.angle) * p.radius + p.vx
    const py = this.center.y + Math.sin(p.angle) * p.radius * 0.5 + p.vy

    const ctx = this.ctx
    ctx.save()
    ctx.globalAlpha = opacity
    ctx.translate(px, py)
    ctx.rotate(p.selfRot)

    ctx.beginPath()
    ctx.roundRect(-w / 2, -h / 2, w, h, Math.max(3, 8 * scale))
    ctx.clip()

    ctx.drawImage(p.img, -w / 2, -h / 2, w, h)
    ctx.restore()
  }

  // ── Events ────────────────────────────────────────────────────────────────

  addListeners() {
    this._onResize = () => this.resize()
    window.addEventListener("resize", this._onResize)
  }
}
